import { useMemo, useCallback } from "react";
import { useTellescopeInfiniteQuery } from "../use-tellescope-infinite-query";
import { type CommonQueryDataOptions, type CommonQueryOptions, type SupportedSession } from "../../types";
import { UseTellescopeInfiniteQueryOptions } from "../types";
import type { TellescopeModelFromName } from "../../mappings/";
import { combineFormResponsesByForm, type FormResponseGroup } from "../../utils";
import { useAISummary, UseAISummaryReturn } from "./use-ai-summary";
import { formatName, getEnduserTimelineAISummaryMetadata, splitEmailContent } from "@tellescope/utils";
import { useTellescopeQuery } from "../use-tellescope-query";
import { useTellescopeQueryClient } from "../use-tellescope-query-client";

export type TimelineItemModel = "phone_calls" | "form_responses" | "sms_messages" | "chat_rooms" | "emails" | "notes";

type TimelineQueryOptions<Model extends TimelineItemModel, TError = Error> = Omit<
  UseTellescopeInfiniteQueryOptions<Model, "getSome", string | undefined, TError>,
  | "model"
  | "method"
  | "request"
  | "paginateRequest"
  | "session"
  | "getNextPageParam"
  | "initialPageParam"
  | "select"
  | "placeholderData"
>;

/**
 * Type definitions for timeline item models
 */
type PhoneCall = TellescopeModelFromName<"phone_calls">;
type SMSMessage = TellescopeModelFromName<"sms_messages">;
type ChatRoom = TellescopeModelFromName<"chat_rooms">;
type Email = TellescopeModelFromName<"emails">;
type Note = TellescopeModelFromName<"notes">;

/**
 * Discriminated union type for all timeline items with type and timestamp fields
 */
export type TimelineItem =
  | (PhoneCall & { _type: "phone_call"; _timestamp: number })
  | (FormResponseGroup & { _type: "form_response_group"; _timestamp: number })
  | (SMSMessage & { _type: "sms_message"; _timestamp: number })
  | (ChatRoom & { _type: "chat_room"; _timestamp: number })
  | (Email & { _type: "email"; _timestamp: number })
  | (Note & { _type: "note"; _timestamp: number });

/**
 * Configuration options for the useEnduserTimelineItems hook
 */
export interface UseEnduserTimelineItemsOptions<TError = Error, TData = TimelineItem[]> {
  /**
   * The enduser ID to fetch timeline items for
   */
  enduserId: string;

  /**
   * Number of items to fetch per page for each source
   * @default 25
   */
  limit?: number;

  /**
   * Optional session to use for the API calls (uses context session if not provided)
   */
  session?: SupportedSession | null;

  /**
   * Tanstack query options to be passed into each of the queries this hook combines
   */
  queryOptions?: CommonQueryOptions &
    CommonQueryDataOptions<TimelineItem[], TData> & {
      [Model in TimelineItemModel]?: TimelineQueryOptions<Model, TError>;
    };
  combineSMSEntries?: boolean;
  locale?: string;
  onOutOfCredits?: () => void;
}

/**
 * Return type for the useEnduserTimelineItems hook
 */
export interface UseEnduserTimelineItemsReturn<TError = Error, TData = TimelineItem[]> {
  data: TData;
  fetchNextPage: () => Promise<void>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
  error: TError | null;
  queries: {
    phoneCalls: ReturnType<typeof useTellescopeInfiniteQuery<"phone_calls", "getSome", string | undefined, TError>>;
    formResponses: ReturnType<
      typeof useTellescopeInfiniteQuery<"form_responses", "getSome", string | undefined, TError>
    >;
    smsMessages: ReturnType<typeof useTellescopeInfiniteQuery<"sms_messages", "getSome", string | undefined, TError>>;
    chatRooms: ReturnType<typeof useTellescopeInfiniteQuery<"chat_rooms", "getSome", string | undefined, TError>>;
    emails: ReturnType<typeof useTellescopeInfiniteQuery<"emails", "getSome", string | undefined, TError>>;
    notes: ReturnType<typeof useTellescopeInfiniteQuery<"notes", "getSome", string | undefined, TError>>;
  };
  rawTimelineItems: TimelineItem[];
  summaryData: Omit<UseAISummaryReturn, "generateSummary"> & {
    /**
     * Generate (or regenerate) the summary. Pass an optional `focus` to steer this single
     * generation — it refines the prompt for this call only and does not affect which existing
     * summary is displayed.
     */
    generateSummary: (args?: { focus?: string }) => Promise<string | undefined>;
  };
}

const AI_SUMMARY_TEXT_MAX_LENGTH = 500;
const AI_SUMMARY_MESSAGE_BUDGET = 23000;

const truncateForSummary = (text: string | undefined | null): string | undefined => {
  if (!text) return undefined;
  if (text.length <= AI_SUMMARY_TEXT_MAX_LENGTH) return text;
  return text.slice(0, AI_SUMMARY_TEXT_MAX_LENGTH) + "...";
};

const stripHtmlTags = (html: string): string =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getLatestEmailReplyText = (
  htmlContent: string | undefined,
  textContent: string | undefined
): string | undefined => {
  if (htmlContent) {
    const { latestContent } = splitEmailContent(htmlContent);
    if (latestContent) return stripHtmlTags(latestContent);
  }
  return textContent;
};

type SummaryItem = { id: string; timestamp: Date; _type: string };

const toPhoneCallSummaryItem = (item: PhoneCall) => ({
  id: item.id,
  _type: "phone_call" as const,
  createdAt: item.createdAt,
  timestamp: item.timestamp ?? item.updatedAt,
  inbound: item.inbound,
  isVoicemail: item.isVoicemail,
  callDurationInSeconds: item.callDurationInSeconds,
  aiSummary: truncateForSummary(item.aiSummary),
});

const toFormResponseSummaryItem = (response: FormResponseGroup["responses"][number]) => ({
  id: response.id,
  timestamp: response.submittedAt ?? response.updatedAt,
  _type: "form_response" as const,
  formTitle: response.formTitle,
  submittedAt: response.submittedAt,
  createdAt: response.createdAt,
});

const toSmsSummaryItem = (item: SMSMessage) => ({
  id: item.id,
  _type: "sms_message" as const,
  createdAt: item.createdAt,
  timestamp: item.timestamp ?? item.updatedAt,
  message: truncateForSummary(item.message),
  inbound: item.inbound,
});

const toChatRoomSummaryItem = (item: ChatRoom) => ({
  id: item.id,
  timestamp: new Date(item.recentMessageSentAt || new Date(item.createdAt).getTime()),
  _type: "chat_room" as const,
  createdAt: item.createdAt,
  title: item.title,
  topic: item.topic,
  type: item.type,
  recentMessage: truncateForSummary(item.recentMessage),
  recentMessageSentAt: item.recentMessageSentAt,
});

const toEmailSummaryItem = (item: Email) => ({
  id: item.id,
  timestamp: item.timestamp ?? item.updatedAt,
  _type: "email" as const,
  createdAt: item.createdAt,
  subject: item.subject,
  textContent: truncateForSummary(getLatestEmailReplyText(item.HTMLContent, item.textContent)),
  inbound: item.inbound,
});

const toNoteSummaryItem = (item: Note) => ({
  id: item.id,
  timestamp: item.updatedAt,
  _type: "note" as const,
  createdAt: item.createdAt,
  title: item.title,
  text: truncateForSummary(item.text),
  type: item.type,
});

function getPinnedTimestamp(item: TimelineItem): number | null {
  if (!("pinnedAt" in item) || !item.pinnedAt || (typeof item.pinnedAt === "string" && item.pinnedAt === "")) {
    return null;
  }
  return new Date(item.pinnedAt).getTime();
}

/**
 * Custom hook that handles querying all items for an enduser's timeline.
 *
 * This hook uses a watermark-based pagination strategy to maintain strict chronological
 * ordering across multiple data sources. It intelligently fetches from the source whose
 * oldest loaded item is newest (above the current watermark), ensuring no gaps in the timeline.
 *
 * Timeline items include:
 * - Phone calls
 * - Form responses
 * - SMS messages
 * - Chat room sessions
 * - Emails
 * - Notes
 *
 * Items marked with `hiddenFromTimeline: true` are automatically filtered out.
 * Pinned items (with `pinnedAt` set) appear at the top, sorted by pin date.
 *
 * @param options - Configuration options for the timeline query
 *
 * @example
 * ```tsx
 * function EnduserTimeline({ enduserId }: { enduserId: string }) {
 *   const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
 *     useEnduserTimelineItems({ enduserId });
 *
 *   if (isLoading) return <div>Loading timeline...</div>;
 *
 *   return (
 *     <div>
 *       {data.map((item) => (
 *         <TimelineItem key={`${item._type}-${item.id}`} item={item} />
 *       ))}
 *       {hasNextPage && (
 *         <button onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
 *           {isFetchingNextPage ? "Loading..." : "Load More"}
 *         </button>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Access individual queries for advanced use cases
 * function EnduserTimelineWithStats({ enduserId }: { enduserId: string }) {
 *   const { data, queries } = useEnduserTimelineItems({ enduserId });
 *
 *   return (
 *     <div>
 *       <div>Total Notes: {queries.notes.data?.pages.flat().length || 0}</div>
 *       <div>Total Calls: {queries.phoneCalls.data?.pages.flat().length || 0}</div>
 *       // Render timeline items here
 *     </div>
 *   );
 * }
 * ```
 */
export const useEnduserTimelineItems = <TError = Error, TData = TimelineItem[]>({
  enduserId,
  limit = 25,
  session,
  queryOptions: {
    phone_calls: phoneCallsQueryOptions,
    form_responses: formResponseQueryOptions,
    sms_messages: smsMessagesQueryOptions,
    chat_rooms: chatRoomsQueryOptions,
    emails: emailsQueryOptions,
    notes: notesQueryOptions,
    select,
    placeholderData,
    ...globalQueryOptions
  } = {},
  combineSMSEntries = false,
  locale,
  onOutOfCredits,
}: UseEnduserTimelineItemsOptions<TError, TData>): UseEnduserTimelineItemsReturn<TError, TData> => {
  // A disabled source keeps whatever pages it fetched before being disabled (`enabled` is not part of
  // the query key), so page presence alone can't tell us whether a source still counts. Every place
  // that merges sources — the shared watermark, the item list, and next-page selection — has to gate
  // on the caller's intent instead, or a filtered-out channel's stale pages would still censor the
  // channels the user asked for.
  // `enabled` may also be a predicate, which can't be evaluated without the Query it would receive.
  // Only an explicit `false` takes a source out of the merge; a predicate is left to the query itself.
  const globallyEnabled = globalQueryOptions.enabled !== false;
  const phoneCallsEnabled = globallyEnabled && phoneCallsQueryOptions?.enabled !== false;
  const formResponsesEnabled = globallyEnabled && formResponseQueryOptions?.enabled !== false;
  const smsMessagesEnabled = globallyEnabled && smsMessagesQueryOptions?.enabled !== false;
  const chatRoomsEnabled = globallyEnabled && chatRoomsQueryOptions?.enabled !== false;
  const emailsEnabled = globallyEnabled && emailsQueryOptions?.enabled !== false;
  const notesEnabled = globallyEnabled && notesQueryOptions?.enabled !== false;

  const isModelEnabled = useMemo<Record<TimelineItemModel, boolean>>(
    () => ({
      phone_calls: phoneCallsEnabled,
      form_responses: formResponsesEnabled,
      sms_messages: smsMessagesEnabled,
      chat_rooms: chatRoomsEnabled,
      emails: emailsEnabled,
      notes: notesEnabled,
    }),
    [phoneCallsEnabled, formResponsesEnabled, smsMessagesEnabled, chatRoomsEnabled, emailsEnabled, notesEnabled]
  );

  // Set up individual infinite queries for each timeline item type
  const phoneCallsQuery = useTellescopeInfiniteQuery<"phone_calls", "getSome", string | undefined, TError>({
    model: "phone_calls",
    method: "getSome",
    request: {
      filter: { enduserId },
      limit,
    },
    session: session ?? undefined,
    getNextPageParam: (lastPage) => (lastPage.length < limit ? undefined : lastPage.at(-1)?.id),
    ...phoneCallsQueryOptions,
    ...globalQueryOptions,
  });

  const formResponsesQuery = useTellescopeInfiniteQuery<"form_responses", "getSome", string | undefined, TError>({
    model: "form_responses",
    method: "getSome",
    request: {
      filter: { enduserId },
      limit,
    },
    session: session ?? undefined,
    getNextPageParam: (lastPage) => (lastPage.length < limit ? undefined : lastPage.at(-1)?.id),
    ...formResponseQueryOptions,
    ...globalQueryOptions,
  });

  const smsMessagesQuery = useTellescopeInfiniteQuery<"sms_messages", "getSome", string | undefined, TError>({
    model: "sms_messages",
    method: "getSome",
    request: {
      filter: { enduserId },
      limit: combineSMSEntries ? 1 : limit,
    },
    session: session ?? undefined,
    getNextPageParam: (lastPage) => (lastPage.length < limit ? undefined : lastPage.at(-1)?.id),
    ...smsMessagesQueryOptions,
    ...globalQueryOptions,
  });

  const chatRoomsQuery = useTellescopeInfiniteQuery<"chat_rooms", "getSome", string | undefined, TError>({
    model: "chat_rooms",
    method: "getSome",
    request: {
      mdbFilter: { enduserIds: enduserId },
      limit,
    },
    session: session ?? undefined,
    getNextPageParam: (lastPage) => (lastPage.length < limit ? undefined : lastPage.at(-1)?.id),
    ...chatRoomsQueryOptions,
    ...globalQueryOptions,
  });

  const emailsQuery = useTellescopeInfiniteQuery<"emails", "getSome", string | undefined, TError>({
    model: "emails",
    method: "getSome",
    request: {
      filter: { enduserId },
      limit,
    },
    session: session ?? undefined,
    getNextPageParam: (lastPage) => (lastPage.length < limit ? undefined : lastPage.at(-1)?.id),
    ...emailsQueryOptions,
    ...globalQueryOptions,
  });

  const notesQuery = useTellescopeInfiniteQuery<"notes", "getSome", string | undefined, TError>({
    model: "notes",
    method: "getSome",
    request: {
      filter: { enduserId },
      limit,
    },
    session: session ?? undefined,
    getNextPageParam: (lastPage) => (lastPage.length < limit ? undefined : lastPage.at(-1)?.id),
    ...notesQueryOptions,
    ...globalQueryOptions,
  });

  // Group form responses by form
  const groupedFormResponses = useMemo(() => {
    const formResponses = (formResponsesQuery.data?.pages.flat() || []).filter(
      (item) => item && (!("hiddenFromTimeline" in item) || !item.hiddenFromTimeline)
    );
    const { forms } = combineFormResponsesByForm(formResponses);
    return forms;
  }, [formResponsesQuery.data?.pages]);

  // Build all pre-transformed timeline items before watermark filtering
  const allTimelineItems = useMemo<TimelineItem[]>(() => {
    const phoneCalls = (phoneCallsQuery.data?.pages.flat() || []).map((item) => ({
      ...item,
      _type: "phone_call" as const,
      _timestamp: item.timestamp ? new Date(item.timestamp).getTime() : new Date(item.createdAt).getTime(),
    }));

    const formResponseGroups = groupedFormResponses.map((group) => {
      const newestTimestamp = group.responses[0]?.submittedAt
        ? new Date(group.responses[0].submittedAt).getTime()
        : new Date(group.responses[0]?.createdAt || 0).getTime();
      return {
        ...group,
        _type: "form_response_group" as const,
        _timestamp: newestTimestamp,
      };
    });

    const smsMessages = (smsMessagesQuery.data?.pages.flat() || []).reduce<
      Array<SMSMessage & { _type: "sms_message"; _timestamp: number }>
    >((acc, item) => {
      if (item && (!("hiddenFromTimeline" in item) || !item.hiddenFromTimeline)) {
        acc.push({
          ...item,
          _type: "sms_message" as const,
          _timestamp: item.timestamp ? new Date(item.timestamp).getTime() : new Date(item.createdAt).getTime(),
        });
      }
      return acc;
    }, []);

    const chatRooms = (chatRoomsQuery.data?.pages.flat() || []).flatMap((item) =>
      item
        ? [
            {
              ...item,
              _type: "chat_room" as const,
              _timestamp: item.recentMessageSentAt || new Date(item.createdAt).getTime(),
            },
          ]
        : []
    );

    const emails = (emailsQuery.data?.pages.flat() || []).reduce<Array<Email & { _type: "email"; _timestamp: number }>>(
      (acc, item) => {
        if (item && (!("hiddenFromTimeline" in item) || !item.hiddenFromTimeline)) {
          acc.push({
            ...item,
            _type: "email" as const,
            _timestamp: item.timestamp ? new Date(item.timestamp).getTime() : new Date(item.createdAt).getTime(),
          });
        }
        return acc;
      },
      []
    );

    const notes = (notesQuery.data?.pages.flat() || []).reduce<Array<Note & { _type: "note"; _timestamp: number }>>(
      (acc, item) => {
        if (item && (!("hiddenFromTimeline" in item) || !item.hiddenFromTimeline)) {
          acc.push({
            ...item,
            _type: "note" as const,
            _timestamp: new Date(item.createdAt).getTime(),
          });
        }
        return acc;
      },
      []
    );

    // A source disabled after it had already fetched keeps its pages in the cache, so drop those
    // items here rather than letting them reach the timeline (and the watermark) as live data.
    const bySource: Array<[TimelineItemModel, TimelineItem[]]> = [
      ["phone_calls", phoneCalls],
      ["form_responses", formResponseGroups],
      ["sms_messages", smsMessages],
      ["chat_rooms", chatRooms],
      ["emails", emails],
      ["notes", notes],
    ];

    return bySource.flatMap(([model, items]) => (isModelEnabled[model] ? items : []));
  }, [
    isModelEnabled,
    phoneCallsQuery.data?.pages,
    smsMessagesQuery.data?.pages,
    chatRoomsQuery.data?.pages,
    emailsQuery.data?.pages,
    notesQuery.data?.pages,
    groupedFormResponses,
  ]);

  const aiSummaryTimelineItems = useMemo(() => {
    let totalLength = 0;

    const pushItem = (acc: SummaryItem[], summaryItem: SummaryItem) => {
      totalLength += JSON.stringify(summaryItem).length;
      acc.push(summaryItem);
    };

    return allTimelineItems.reduce<SummaryItem[]>((acc, item) => {
      if (totalLength >= AI_SUMMARY_MESSAGE_BUDGET) return acc;

      switch (item._type) {
        case "phone_call":
          pushItem(acc, toPhoneCallSummaryItem(item));
          break;
        case "form_response_group":
          for (const response of item.responses) {
            if (totalLength >= AI_SUMMARY_MESSAGE_BUDGET) break;
            pushItem(acc, toFormResponseSummaryItem(response));
          }
          break;
        case "sms_message":
          pushItem(acc, toSmsSummaryItem(item));
          break;
        case "chat_room":
          pushItem(acc, toChatRoomSummaryItem(item));
          break;
        case "email":
          pushItem(acc, toEmailSummaryItem(item));
          break;
        case "note":
          pushItem(acc, toNoteSummaryItem(item));
          break;
      }
      return acc;
    }, []);
  }, [allTimelineItems]);

  // Merge and filter items based on watermark
  const mergedData = useMemo<TData>(() => {
    const fallbackDataTransformation = (data: TimelineItem[]) => data as TData;
    const dataTranform = select ?? fallbackDataTransformation;

    /**
     * The timestamp at which a source constrains the shared watermark, or `null` when it doesn't:
     * a disabled source (its cached pages are no longer part of the timeline), one with no pages
     * yet, or one that is exhausted (last page shorter than `limit`, so nothing older is coming).
     */
    const getSourceWatermarkBound = <Source extends TimelineItemModel>(
      model: Source,
      pages: TellescopeModelFromName<Source>[][] | undefined,
      getTimestamp: (item: TellescopeModelFromName<Source>) => number
    ) => {
      if (!isModelEnabled[model]) return null;
      const lastPage = (pages ?? []).at(-1)?.filter((item) => item) ?? [];
      if (lastPage.length === 0 || lastPage.length < limit) return null;
      return Math.min(...lastPage.map(getTimestamp));
    };

    const timestampOrCreatedAt = (item: { timestamp?: Date; createdAt: Date }) =>
      item.timestamp ? new Date(item.timestamp).getTime() : new Date(item.createdAt).getTime();

    const oldestTimestamps = [
      getSourceWatermarkBound("phone_calls", phoneCallsQuery.data?.pages, timestampOrCreatedAt),
      getSourceWatermarkBound("form_responses", formResponsesQuery.data?.pages, (item) =>
        item.submittedAt ? new Date(item.submittedAt).getTime() : new Date(item.createdAt).getTime()
      ),
      getSourceWatermarkBound("sms_messages", smsMessagesQuery.data?.pages, timestampOrCreatedAt),
      getSourceWatermarkBound(
        "chat_rooms",
        chatRoomsQuery.data?.pages,
        (item) => item.recentMessageSentAt || new Date(item.createdAt).getTime()
      ),
      getSourceWatermarkBound("emails", emailsQuery.data?.pages, timestampOrCreatedAt),
      getSourceWatermarkBound("notes", notesQuery.data?.pages, (item) => new Date(item.createdAt).getTime()),
    ].filter((ts): ts is number => ts !== null);

    // Watermark is the MAXIMUM (newest) of all the oldest timestamps from sources that still have pages
    // Sources with fewer than `limit` items in last page are exhausted and don't constrain the watermark
    // If no sources have constraining data, watermark is 0 (show everything)
    const watermark = oldestTimestamps.length > 0 ? Math.max(...oldestTimestamps) : 0;

    // Filter to only show items newer than or equal to the watermark, and separate into pinned/regular
    const { pinnedItems, regularItems } = allTimelineItems.reduce<{
      pinnedItems: TimelineItem[];
      regularItems: TimelineItem[];
    }>(
      (acc, item) => {
        const timestamp = item._timestamp;
        if (timestamp >= watermark) {
          const pinnedTimestamp = getPinnedTimestamp(item);
          if (pinnedTimestamp !== null) {
            acc.pinnedItems.push(item);
          } else {
            acc.regularItems.push(item);
          }
        }
        return acc;
      },
      { pinnedItems: [], regularItems: [] }
    );

    // Sort pinned items by pinnedAt (newest first)
    pinnedItems.sort((a, b) => {
      const pinnedA = getPinnedTimestamp(a) || 0;
      const pinnedB = getPinnedTimestamp(b) || 0;
      return pinnedB - pinnedA;
    });

    // Sort regular items by timestamp (newest first)
    regularItems.sort((a, b) => b._timestamp - a._timestamp);

    return dataTranform([...pinnedItems, ...regularItems]);
  }, [
    select,
    allTimelineItems,
    isModelEnabled,
    limit,
    phoneCallsQuery.data?.pages,
    formResponsesQuery.data?.pages,
    smsMessagesQuery.data?.pages,
    chatRoomsQuery.data?.pages,
    emailsQuery.data?.pages,
    notesQuery.data?.pages,
  ]);

  // Intelligent fetchNextPage that determines which source to fetch from
  const fetchNextPage = useCallback(async () => {
    // For each source with more pages, get the oldest loaded item's timestamp
    const sources = [
      {
        name: "phoneCalls",
        model: "phone_calls" as const,
        query: phoneCallsQuery,
        getOldestTimestamp: () => {
          const items = phoneCallsQuery.data?.pages.flat().filter((item) => item) || [];
          if (items.length === 0) return null;
          const timestamps = items.map((item) =>
            item.timestamp ? new Date(item.timestamp).getTime() : new Date(item.createdAt).getTime()
          );
          return Math.min(...timestamps);
        },
        getLastPageOldestTimestamp: () => {
          const pages = phoneCallsQuery.data?.pages || [];
          if (pages.length === 0) return null;
          const lastPage = pages[pages.length - 1]?.filter((item) => item) || [];
          if (lastPage.length === 0) return null;
          const timestamps = lastPage.map((item) =>
            item.timestamp ? new Date(item.timestamp).getTime() : new Date(item.createdAt).getTime()
          );
          return Math.min(...timestamps);
        },
      },
      {
        name: "formResponses",
        model: "form_responses" as const,
        query: formResponsesQuery,
        getOldestTimestamp: () => {
          const items = formResponsesQuery.data?.pages.flat().filter((item) => item) || [];
          if (items.length === 0) return null;
          const timestamps = items.map((item) =>
            item.submittedAt ? new Date(item.submittedAt).getTime() : new Date(item.createdAt).getTime()
          );
          return Math.min(...timestamps);
        },
        getLastPageOldestTimestamp: () => {
          const pages = formResponsesQuery.data?.pages || [];
          if (pages.length === 0) return null;
          const lastPage = pages[pages.length - 1]?.filter((item) => item) || [];
          if (lastPage.length === 0) return null;
          const timestamps = lastPage.map((item) =>
            item.submittedAt ? new Date(item.submittedAt).getTime() : new Date(item.createdAt).getTime()
          );
          return Math.min(...timestamps);
        },
      },
      {
        name: "smsMessages",
        model: "sms_messages" as const,
        query: smsMessagesQuery,
        getOldestTimestamp: () => {
          const items = smsMessagesQuery.data?.pages.flat().filter((item) => item) || [];
          if (items.length === 0) return null;
          const timestamps = items.map((item) =>
            item.timestamp ? new Date(item.timestamp).getTime() : new Date(item.createdAt).getTime()
          );
          return Math.min(...timestamps);
        },
        getLastPageOldestTimestamp: () => {
          const pages = smsMessagesQuery.data?.pages || [];
          if (pages.length === 0) return null;
          const lastPage = pages[pages.length - 1]?.filter((item) => item) || [];
          if (lastPage.length === 0) return null;
          const timestamps = lastPage.map((item) =>
            item.timestamp ? new Date(item.timestamp).getTime() : new Date(item.createdAt).getTime()
          );
          return Math.min(...timestamps);
        },
      },
      {
        name: "chatRooms",
        model: "chat_rooms" as const,
        query: chatRoomsQuery,
        getOldestTimestamp: () => {
          const items = chatRoomsQuery.data?.pages.flat().filter((item) => item) || [];
          if (items.length === 0) return null;
          const timestamps = items.map((item) => item.recentMessageSentAt || new Date(item.createdAt).getTime());
          return Math.min(...timestamps);
        },
        getLastPageOldestTimestamp: () => {
          const pages = chatRoomsQuery.data?.pages || [];
          if (pages.length === 0) return null;
          const lastPage = pages[pages.length - 1]?.filter((item) => item) || [];
          if (lastPage.length === 0) return null;
          const timestamps = lastPage.map((item) => item.recentMessageSentAt || new Date(item.createdAt).getTime());
          return Math.min(...timestamps);
        },
      },
      {
        name: "emails",
        model: "emails" as const,
        query: emailsQuery,
        getOldestTimestamp: () => {
          const items = emailsQuery.data?.pages.flat().filter((item) => item) || [];
          if (items.length === 0) return null;
          const timestamps = items.map((item) =>
            item.timestamp ? new Date(item.timestamp).getTime() : new Date(item.createdAt).getTime()
          );
          return Math.min(...timestamps);
        },
        getLastPageOldestTimestamp: () => {
          const pages = emailsQuery.data?.pages || [];
          if (pages.length === 0) return null;
          const lastPage = pages[pages.length - 1]?.filter((item) => item) || [];
          if (lastPage.length === 0) return null;
          const timestamps = lastPage.map((item) =>
            item.timestamp ? new Date(item.timestamp).getTime() : new Date(item.createdAt).getTime()
          );
          return Math.min(...timestamps);
        },
      },
      {
        name: "notes",
        model: "notes" as const,
        query: notesQuery,
        getOldestTimestamp: () => {
          const items = notesQuery.data?.pages.flat().filter((item) => item) || [];
          if (items.length === 0) return null;
          const timestamps = items.map((item) => new Date(item.createdAt).getTime());
          return Math.min(...timestamps);
        },
        getLastPageOldestTimestamp: () => {
          const pages = notesQuery.data?.pages || [];
          if (pages.length === 0) return null;
          const lastPage = pages[pages.length - 1]?.filter((item) => item) || [];
          if (lastPage.length === 0) return null;
          const timestamps = lastPage.map((item) => new Date(item.createdAt).getTime());
          return Math.min(...timestamps);
        },
      },
    ];

    // Find the source whose oldest loaded item is newest (and has more pages)
    const eligibleSources = sources.flatMap((source) => {
      if (!isModelEnabled[source.model] || !source.query.hasNextPage) return [];
      const oldestTimestamp = source.getOldestTimestamp();
      return oldestTimestamp !== null ? [{ ...source, oldestTimestamp }] : [];
    });

    if (eligibleSources.length === 0) {
      return;
    }

    // Sort by oldest timestamp descending (newest oldest item first)
    eligibleSources.sort((a, b) => b.oldestTimestamp - a.oldestTimestamp);

    // Fetch from the source with the newest oldest item
    const sourceToFetch = eligibleSources[0];
    if (sourceToFetch) {
      await sourceToFetch.query.fetchNextPage();
    }
  }, [isModelEnabled, phoneCallsQuery, formResponsesQuery, smsMessagesQuery, chatRoomsQuery, emailsQuery, notesQuery]);

  const getCombinedQueryDataBoolean = useCallback(
    (key: keyof typeof phoneCallsQuery): boolean => {
      return [
        [isModelEnabled.phone_calls, phoneCallsQuery[key]],
        [isModelEnabled.form_responses, formResponsesQuery[key]],
        [isModelEnabled.sms_messages, smsMessagesQuery[key]],
        [isModelEnabled.chat_rooms, chatRoomsQuery[key]],
        [isModelEnabled.emails, emailsQuery[key]],
        [isModelEnabled.notes, notesQuery[key]],
      ].some(([enabled, value]) => enabled && !!value);
    },
    [isModelEnabled, chatRoomsQuery, emailsQuery, formResponsesQuery, notesQuery, phoneCallsQuery, smsMessagesQuery]
  );

  // Aggregate loading and error states
  const { isLoading, isFetchingNextPage, hasNextPage, isError } = useMemo(
    () => ({
      isLoading: getCombinedQueryDataBoolean("isLoading"),
      isFetchingNextPage: getCombinedQueryDataBoolean("isFetchingNextPage"),
      hasNextPage: getCombinedQueryDataBoolean("hasNextPage"),
      isError: getCombinedQueryDataBoolean("isError"),
    }),
    [getCombinedQueryDataBoolean]
  );

  const error =
    phoneCallsQuery.error ||
    formResponsesQuery.error ||
    smsMessagesQuery.error ||
    chatRoomsQuery.error ||
    emailsQuery.error ||
    notesQuery.error;

  const dataToReturn = useMemo(() => {
    if (!isLoading || !placeholderData) {
      return mergedData;
    }

    const isEmpty = Array.isArray(mergedData) ? !mergedData.length : !mergedData;
    if (!isEmpty) {
      return mergedData;
    }

    if (placeholderData instanceof Function) {
      return placeholderData(undefined, undefined);
    }

    return placeholderData;
  }, [isLoading, mergedData, placeholderData]);

  const queryClient = useTellescopeQueryClient(session ?? undefined);

  const { data: enduser } = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: enduserId,
    enabled: !!enduserId,
  });
  // For the type hash, limit SMS to only the most recent to keep the hash stable
  // regardless of the combineSMSEntries setting or how many SMS are loaded
  const aiSummaryHashItems = useMemo(() => {
    const mostRecentSms = aiSummaryTimelineItems.find((item) => item._type === "sms_message");
    return aiSummaryTimelineItems.filter((item) => item._type !== "sms_message" || item.id === mostRecentSms?.id);
  }, [aiSummaryTimelineItems]);

  const aiSummaryMetadata = useMemo(() => {
    if (!enduser) return undefined;

    const enduserName = formatName(enduser);

    const { type, prompt } = getEnduserTimelineAISummaryMetadata({
      enduserId,
      enduserName,
      timelineItems: aiSummaryHashItems,
      locale,
    });

    const { message } = getEnduserTimelineAISummaryMetadata({
      enduserId,
      enduserName,
      timelineItems: aiSummaryTimelineItems,
      locale,
    });

    return { type, prompt, message };
  }, [enduser, enduserId, locale, aiSummaryTimelineItems, aiSummaryHashItems]);
  const summaryData = useAISummary({
    type: aiSummaryMetadata?.type ?? "",
    prompt: aiSummaryMetadata?.prompt ?? "",
    enabled: !!aiSummaryMetadata,
    onOutOfCredits,
  });

  const generateSummary = useCallback<UseEnduserTimelineItemsReturn["summaryData"]["generateSummary"]>(
    async ({ focus } = {}) => {
      if (!aiSummaryMetadata?.message || !enduser) return undefined;

      const enduserName = formatName(enduser);

      // Focus only steers a single generation, so build the prompt/message with it here rather than
      // baking it into the hook-level metadata (which keys the displayed summary's lookup `type`).
      let message = focus
        ? getEnduserTimelineAISummaryMetadata({
            enduserId,
            enduserName,
            timelineItems: aiSummaryTimelineItems,
            locale,
            focus,
          }).message
        : aiSummaryMetadata.message;

      const prompt = focus
        ? getEnduserTimelineAISummaryMetadata({
            enduserId,
            enduserName,
            timelineItems: aiSummaryHashItems,
            locale,
            focus,
          }).prompt
        : undefined;

      if (combineSMSEntries && enduser) {
        try {
          const smsData = await queryClient.fetchInfiniteQuery({
            model: "sms_messages",
            method: "getSome",
            request: {
              filter: { enduserId },
              limit: 20,
            },
            getNextPageParam: () => undefined,
            initialPageParam: undefined,
          });

          const fetchedSmsItems = (smsData.pages.flat() || []).reduce<SummaryItem[]>((acc, item) => {
            if (item && (!("hiddenFromTimeline" in item) || !item.hiddenFromTimeline)) {
              acc.push(toSmsSummaryItem(item));
            }
            return acc;
          }, []);

          const fetchedSmsIds = new Set(fetchedSmsItems.map((item) => item.id));
          const nonDuplicateItems = aiSummaryTimelineItems.filter((item) => !fetchedSmsIds.has(item.id));
          const mergedItems = [...nonDuplicateItems, ...fetchedSmsItems];

          const metadata = getEnduserTimelineAISummaryMetadata({
            enduserId,
            enduserName,
            timelineItems: mergedItems,
            locale,
            focus,
          });

          message = metadata.message;
        } catch {
          // Fall back to existing metadata if fetch fails
        }
      }

      return summaryData.generateSummary({ message, prompt });
    },
    [
      aiSummaryMetadata?.message,
      aiSummaryHashItems,
      combineSMSEntries,
      enduser,
      queryClient,
      enduserId,
      aiSummaryTimelineItems,
      locale,
      summaryData,
    ]
  );

  return {
    data: dataToReturn,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    queries: {
      phoneCalls: phoneCallsQuery,
      formResponses: formResponsesQuery,
      smsMessages: smsMessagesQuery,
      chatRooms: chatRoomsQuery,
      emails: emailsQuery,
      notes: notesQuery,
    },
    rawTimelineItems: allTimelineItems,
    summaryData: {
      ...summaryData,
      generateSummary,
    },
  };
};
